import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { HFP_V3_SCHEMAS, HFP_V3_SCHEMAS_LIST } from '../schemas/hfpV3';
import HfpV2Report from '../components/hfp/HfpV2Report';
import HfpV3OverviewMatrix from '../components/hfp/HfpV3OverviewMatrix';
import buildHfpV3ReportModel from '../utils/hfpV3ReportAdapter';

// ────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ────────────────────────────────────────────────────────────────────────────
// The v3 counterpart of HfpV2DemoPage: same methodology (fill or simulate
// both respondents' forms, run the production scoring engine via a transient
// callable, then generate the full report), rebound to the 157-item v3
// instrument. Scoring renders through the v3 overview matrix and the report
// through the same adapter + HfpV2Report pipeline the clinician profile uses.

const STORAGE_KEY = 'hfp-v3-demo-answers';
const RESPONDENT_KEY = 'hfp-v3-demo-respondent';
const SCORING_KEY = 'hfp-v3-demo-scoring';
const REPORT_KEY = 'hfp-v3-demo-report';

const RESPONDENT_LABELS = {
  home: 'Parent / Guardian (Home)',
  school: 'Teacher (School)',
};

/**
 * Simulation profiles control the distribution of randomly-generated answers
 * (same shapes as the v2 demo). Weights correspond to answer option indices
 * (0 = most positive, last = most negative); the picker normalises them.
 * v3 scales are 6-option capacity items, 5-option environmental items and
 * short conditional follow-ups (handled separately).
 */
const SIMULATION_PROFILES = {
  typical: { label: 'Typical', weights6: [5, 15, 60, 10, 7, 3], weights5: [5, 15, 60, 15, 5] },
  efDominant: { label: 'EF-Dominant Difficulty', weights6: [2, 5, 20, 30, 30, 13], weights5: [2, 10, 20, 40, 28] },
  sensoryCascade: { label: 'Sensory Cascade', weights6: [3, 8, 30, 25, 22, 12], weights5: [5, 20, 30, 30, 15] },
  pervasive: { label: 'Pervasive Difficulty', weights6: [1, 3, 10, 25, 35, 26], weights5: [2, 5, 15, 40, 38] },
  strength: { label: 'Broad Strengths', weights6: [30, 35, 25, 7, 2, 1], weights5: [35, 30, 25, 7, 3] },
};

/**
 * Context agreement presets — the two-layer (systematic per-form shift +
 * per-item noise) divergence model, unchanged from the v2 demo so reviewers
 * get comparable Home-vs-School behaviour.
 */
const CONTEXT_AGREEMENT_PRESETS = {
  high: {
    label: 'High Agreement',
    domainShiftProb: 0.15, domainShiftMin: 0, domainShiftMax: 1,
    directionBias: 0.5, itemNoise: 1, itemNoiseProb: 0.1,
  },
  moderate: {
    label: 'Moderate Divergence',
    domainShiftProb: 0.6, domainShiftMin: 1, domainShiftMax: 2,
    directionBias: 0.6, itemNoise: 1, itemNoiseProb: 0.3,
  },
  low: {
    label: 'Low Agreement',
    domainShiftProb: 0.85, domainShiftMin: 2, domainShiftMax: 4,
    directionBias: 0.65, itemNoise: 1, itemNoiseProb: 0.4,
  },
};

// The live model list is fetched from the shared rmsHfpV2DemoModels callable
// (both demo pages read the same llm_router registry); this is the fallback.
const FALLBACK_LLM_MODELS = [
  { id: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash Lite (Vertex AI, EU)', provider: 'vertex' },
];
const FALLBACK_MODEL_ID = 'gemini-3.1-flash-lite';

const PRONOUN_OPTIONS = [
  { id: 'they/them', label: 'They / them' },
  { id: 'he/him', label: 'He / him' },
  { id: 'she/her', label: 'She / her' },
];

// ────────────────────────────────────────────────────────────────────────────
// STORAGE HELPERS
// ────────────────────────────────────────────────────────────────────────────

function loadAnswers() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { home: {}, school: {} };
  } catch {
    return { home: {}, school: {} };
  }
}

function saveAnswers(answers) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(answers));
  } catch { /* quota exceeded – non-critical for demo */ }
}

function loadRespondent() {
  try {
    return localStorage.getItem(RESPONDENT_KEY) || 'home';
  } catch {
    return 'home';
  }
}

function loadSessionData(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveSessionData(key, data) {
  try {
    if (data == null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(data));
    }
  } catch { /* quota exceeded – non-critical for demo */ }
}

function clearAllSessionData() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(RESPONDENT_KEY);
    localStorage.removeItem(SCORING_KEY);
    localStorage.removeItem(REPORT_KEY);
  } catch { /* noop */ }
}

// ────────────────────────────────────────────────────────────────────────────
// QUESTION VISIBILITY + SIMULATION HELPERS
// ────────────────────────────────────────────────────────────────────────────

/**
 * v3 respondent scoping: "both" is always visible; "lg_only" maps to the
 * home respondent. (The v3 recut has no school_only items, but the mapping
 * is kept for parity with the v2 demo.)
 */
function isVisibleForRespondent(question, respondent) {
  const r = question?.meta?.hfp?.respondent || 'both';
  if (r === 'both') return true;
  if (r === 'lg_only' && respondent === 'home') return true;
  if (r === 'school_only' && respondent === 'school') return true;
  return false;
}

/**
 * A question gated behind another answer (the sensory threshold follow-ups:
 * strategy_seek / strategy_reaction only exist when the threshold answer is
 * at its extreme). Mirrors the production conditional gating.
 */
function conditionActive(question, formAnswers) {
  const cond = question?.meta?.hfp?.conditionalOn;
  if (!cond) return true;
  return formAnswers?.[cond.questionId] === cond.value;
}

function weightedRandom(weights) {
  const total = weights.reduce((a, b) => a + b, 0);
  let roll = Math.random() * total;
  for (let i = 0; i < weights.length; i++) {
    roll -= weights[i];
    if (roll <= 0) return i;
  }
  return weights.length - 1;
}

// ────────────────────────────────────────────────────────────────────────────
// SMALL PRESENTATIONAL COMPONENTS (ported from the v2 demo)
// ────────────────────────────────────────────────────────────────────────────

function StatusBadge({ progress }) {
  if (progress === 100) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        Complete
      </span>
    );
  }
  if (progress > 0) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        {progress}%
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
      Not started
    </span>
  );
}

function JsonPanel({ title, data, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <span className="text-sm font-medium text-gray-700">{title}</span>
        <span className="text-xs text-gray-400">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <pre className="p-4 text-[11px] leading-relaxed text-gray-800 bg-white overflow-x-auto max-h-96 overflow-y-auto">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}

/**
 * One form, one section at a time — the same walk-through the v2 demo uses.
 * Conditional follow-ups (sensory threshold gates) appear only when their
 * gate answer matches, exactly as in production.
 */
function DemoFormView({ schema, answers, setAnswer, respondent, onClose }) {
  const [activeSectionIdx, setActiveSectionIdx] = useState(0);

  const visibleSections = useMemo(() => {
    return (schema?.sections || []).filter((sec) =>
      (sec.questions || []).some((q) => isVisibleForRespondent(q, respondent))
    );
  }, [schema, respondent]);

  const currentSection = visibleSections[activeSectionIdx];
  const visibleQuestions = useMemo(() => {
    return (currentSection?.questions || []).filter((q) => {
      if (!isVisibleForRespondent(q, respondent)) return false;
      return conditionActive(q, answers);
    });
  }, [currentSection, respondent, answers]);

  const framingQuestion = (
    visibleQuestions.find((q) => !q.meta?.hfp?.conditionalOn)?.meta?.hfp?.framingQuestion || ''
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-primary/5 to-primary/10 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{schema.title}</h3>
            <p className="text-sm text-gray-600 mt-0.5">
              Section {activeSectionIdx + 1} of {visibleSections.length}: {currentSection?.title}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white/60 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {framingQuestion && (
        <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
          <p className="text-sm font-medium text-blue-800 italic">&ldquo;{framingQuestion}&rdquo;</p>
        </div>
      )}

      <div className="px-6 py-4 space-y-6 max-h-[60vh] overflow-y-auto">
        {visibleQuestions.map((q, qIdx) => {
          const currentAnswer = answers?.[q.questionId];
          const isFollowUp = Boolean(q.meta?.hfp?.conditionalOn);
          const showFollowUpHeading = isFollowUp && (
            qIdx === 0 || !visibleQuestions[qIdx - 1]?.meta?.hfp?.conditionalOn
          );
          return (
            <div key={q.questionId}>
              {showFollowUpHeading && q.meta?.hfp?.framingQuestion && (
                <div className="mb-3 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-lg">
                  <p className="text-xs font-medium text-indigo-700 italic">{q.meta.hfp.framingQuestion}</p>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-gray-900 mb-2">
                  <span className="text-xs text-gray-400 mr-1.5">{q.meta?.hfp?.itemCode}</span>
                  {q.label}
                </p>
                <div className="flex flex-wrap gap-2">
                  {(q.options || []).map((opt) => {
                    const selected = currentAnswer === opt.value;
                    return (
                      <button
                        key={opt.optionId}
                        type="button"
                        onClick={() => setAnswer(schema.formKey, q.questionId, opt.value)}
                        className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${
                          selected
                            ? 'bg-primary text-white border-primary shadow-sm'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-primary/50 hover:bg-primary/5'
                        }`}
                      >
                        {opt.label}
                        {opt.meta?.score != null && <span className="opacity-60"> ({opt.meta.score})</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
        <button
          type="button"
          disabled={activeSectionIdx === 0}
          onClick={() => setActiveSectionIdx((i) => Math.max(0, i - 1))}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Previous
        </button>
        <span className="text-xs text-gray-500">
          {activeSectionIdx + 1} / {visibleSections.length}
        </span>
        {activeSectionIdx < visibleSections.length - 1 ? (
          <button
            type="button"
            onClick={() => setActiveSectionIdx((i) => Math.min(visibleSections.length - 1, i + 1))}
            className="px-4 py-2 text-sm font-medium text-white bg-primary border border-primary rounded-lg hover:bg-primary/90 transition-colors"
          >
            Next
          </button>
        ) : (
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 transition-colors"
          >
            Done
          </button>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// PAGE
// ────────────────────────────────────────────────────────────────────────────

export default function HfpV3DemoPage() {
  const [allAnswers, setAllAnswers] = useState(loadAnswers);
  const [respondent, setRespondent] = useState(loadRespondent);
  const [openFormKey, setOpenFormKey] = useState(null);
  const [simProfile, setSimProfile] = useState('typical');
  const [simAgreement, setSimAgreement] = useState('high');
  const [scoringResult, setScoringResult] = useState(() => loadSessionData(SCORING_KEY));
  const [scoringLoading, setScoringLoading] = useState(false);
  const [scoringError, setScoringError] = useState(null);
  const [reportResult, setReportResult] = useState(() => loadSessionData(REPORT_KEY));
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);

  const [childName, setChildName] = useState('Alex');
  const [pronouns, setPronouns] = useState('they/them');

  const [llmModels, setLlmModels] = useState(FALLBACK_LLM_MODELS);
  const [selectedModel, setSelectedModel] = useState(FALLBACK_MODEL_ID);
  const [fullScreen, setFullScreen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { httpsCallable } = await import('firebase/functions');
        const { functions } = await import('../../firebase');
        // Shared with the v2 demo — both pages read the same router registry.
        const modelsFn = httpsCallable(functions, 'rmsHfpV2DemoModels');
        const res = await modelsFn({});
        if (cancelled) return;
        const models = res?.data?.models;
        if (Array.isArray(models) && models.length > 0) {
          setLlmModels(models);
          const fallback = res?.data?.default || models[0].id;
          setSelectedModel((prev) => (models.some((m) => m.id === prev) ? prev : fallback));
        }
      } catch (err) {
        console.warn('Could not load LLM model list, using fallback:', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { saveAnswers(allAnswers); }, [allAnswers]);
  useEffect(() => {
    try { localStorage.setItem(RESPONDENT_KEY, respondent); } catch { /* noop */ }
  }, [respondent]);
  useEffect(() => { saveSessionData(SCORING_KEY, scoringResult); }, [scoringResult]);
  useEffect(() => { saveSessionData(REPORT_KEY, reportResult); }, [reportResult]);

  const currentAnswers = useMemo(() => allAnswers[respondent] || {}, [allAnswers, respondent]);

  const setAnswer = useCallback((formKey, questionId, value) => {
    setAllAnswers((prev) => ({
      ...prev,
      [respondent]: {
        ...(prev[respondent] || {}),
        [formKey]: {
          ...((prev[respondent] || {})[formKey] || {}),
          [questionId]: value,
        },
      },
    }));
  }, [respondent]);

  const resetAll = useCallback(() => {
    setAllAnswers({ home: {}, school: {} });
    setScoringResult(null);
    setReportResult(null);
    clearAllSessionData();
  }, []);

  // Per-form progress: conditional follow-ups only count toward the total
  // when their gate answer makes them visible (mirrors production progress).
  const formProgress = useMemo(() => {
    const progress = {};
    HFP_V3_SCHEMAS_LIST.forEach((schema) => {
      const formAnswers = currentAnswers[schema.formKey] || {};
      let total = 0;
      let answered = 0;
      (schema.sections || []).forEach((sec) => {
        (sec.questions || []).forEach((q) => {
          if (!isVisibleForRespondent(q, respondent)) return;
          if (!conditionActive(q, formAnswers)) return;
          total++;
          if (formAnswers[q.questionId] != null) answered++;
        });
      });
      progress[schema.formKey] = total > 0 ? Math.round((answered / total) * 100) : 0;
    });
    return progress;
  }, [currentAnswers, respondent]);

  // Simulation — the v2 demo's two-layer divergence model over the v3
  // instrument. Questions are generated in schema order so threshold gates
  // are answered before their conditional follow-ups.
  const simulateAll = useCallback(() => {
    const profile = SIMULATION_PROFILES[simProfile] || SIMULATION_PROFILES.typical;
    const agreement = CONTEXT_AGREEMENT_PRESETS[simAgreement] || CONTEXT_AGREEMENT_PRESETS.high;
    const generated = { home: {}, school: {} };

    HFP_V3_SCHEMAS_LIST.forEach((schema) => {
      generated.home[schema.formKey] = {};
      generated.school[schema.formKey] = {};

      let formShift = 0;
      if (Math.random() < agreement.domainShiftProb) {
        const range = agreement.domainShiftMax - agreement.domainShiftMin;
        formShift = agreement.domainShiftMin + Math.round(Math.random() * range);
        const dir = Math.random() < agreement.directionBias ? 1 : -1;
        formShift *= dir;
      }

      (schema.sections || []).forEach((sec) => {
        (sec.questions || []).forEach((q) => {
          const options = q.options || [];
          if (options.length === 0) return;

          const homeVisible = isVisibleForRespondent(q, 'home');
          const schoolVisible = isVisibleForRespondent(q, 'school');
          const cond = q.meta?.hfp?.conditionalOn;

          // Conditional follow-ups (sensory threshold gates): only answer
          // when the gate answer matches, picking uniformly between the
          // short follow-up options (e.g. Yes/No seek, Active/Passive).
          if (cond) {
            const gateHome = generated.home[schema.formKey]?.[cond.questionId];
            const gateSchool = generated.school[schema.formKey]?.[cond.questionId];
            const pick = () => options[Math.floor(Math.random() * options.length)].value;
            if (homeVisible && gateHome === cond.value) {
              generated.home[schema.formKey][q.questionId] = pick();
            }
            if (schoolVisible && gateSchool === cond.value) {
              generated.school[schema.formKey][q.questionId] = pick();
            }
            return;
          }

          // Standard scale questions: weighted by profile, then the school
          // answer derived from home via the two-layer shift model.
          const weights =
            options.length >= 6 ? profile.weights6
              : options.length === 5 ? profile.weights5
                : options.map(() => 1);

          const homeIdx = weightedRandom(weights);
          const safeHomeIdx = Math.min(homeIdx, options.length - 1);

          if (homeVisible) {
            generated.home[schema.formKey][q.questionId] = options[safeHomeIdx].value;
          }
          if (schoolVisible) {
            let totalShift = formShift;
            if (Math.random() < agreement.itemNoiseProb) {
              const noise = Math.ceil(Math.random() * agreement.itemNoise);
              totalShift += (Math.random() < 0.5 ? noise : -noise);
            }
            const schoolIdx = Math.max(0, Math.min(options.length - 1, safeHomeIdx + totalShift));
            generated.school[schema.formKey][q.questionId] = options[schoolIdx].value;
          }
        });
      });
    });

    setAllAnswers(generated);
    setScoringResult(null);
    setReportResult(null);
  }, [simProfile, simAgreement]);

  // Scoring — the production v3 pipeline via the transient demo callable.
  const runScoring = useCallback(async () => {
    setScoringLoading(true);
    setScoringError(null);
    try {
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../../firebase');
      const scoreFn = httpsCallable(functions, 'rmsHfpV3DemoScore');
      const result = await scoreFn({
        homeAnswers: allAnswers.home || {},
        schoolAnswers: allAnswers.school || {},
      });
      if (result.data?.error) {
        setScoringError(result.data.error);
        setScoringResult(null);
      } else {
        setScoringResult(result.data);
        setReportResult(null);
      }
    } catch (err) {
      console.warn('Scoring cloud function error:', err.message, err.code, err.details);
      setScoringError(`Cloud function error: ${err.message}`);
    } finally {
      setScoringLoading(false);
    }
  }, [allAnswers]);

  // Report — the full narrative payload (writer + editor + adjustments with
  // why-this-matters), rendered through the same adapter + HfpV2Report the
  // clinician profile preview uses.
  const generateReport = useCallback(async () => {
    if (!scoringResult) return;
    setReportLoading(true);
    setReportError(null);
    try {
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../../firebase');
      const reportFn = httpsCallable(functions, 'rmsHfpV3DemoReport', { timeout: 540000 });
      const result = await reportFn({
        homeAnswers: allAnswers.home || {},
        schoolAnswers: allAnswers.school || {},
        childName: (childName || '').trim() || 'the child',
        pronouns,
        modelId: selectedModel,
      });
      if (result.data?.error) {
        setReportError(result.data.error);
        setReportResult(null);
      } else {
        setReportResult(result.data);
      }
    } catch (err) {
      console.warn('Report cloud function error:', err.message, err.code, err.details);
      setReportError(`Cloud function error: ${err.message}`);
    } finally {
      setReportLoading(false);
    }
  }, [allAnswers, scoringResult, selectedModel, childName, pronouns]);

  const openSchema = openFormKey ? HFP_V3_SCHEMAS[openFormKey] : null;
  const reportModel = useMemo(() => {
    if (!reportResult?.scoring || !reportResult?.narratives) return null;
    try {
      return buildHfpV3ReportModel(
        reportResult.scoring,
        (childName || '').trim() || 'the child',
        reportResult.narratives,
      );
    } catch (err) {
      console.warn('Report model build failed:', err.message);
      return null;
    }
  }, [reportResult, childName]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {!fullScreen && (
        <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-gray-200">
          <div className="mx-auto px-6 py-4" style={{ maxWidth: '80%' }}>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-gray-900">HFP v3 Demo</h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  Internal review – Holistic Functional Profile v3 (157-item instrument)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={resetAll}
                  className="px-3 py-1.5 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  Reset All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`mx-auto px-6 py-8 space-y-8${fullScreen ? ' hidden' : ''}`} style={{ maxWidth: '80%' }}>
        {/* ───── Respondent Toggle ───── */}
        <div className="flex items-center gap-3 bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <span className="text-sm font-medium text-gray-700">Respondent:</span>
          <div className="flex bg-gray-100 rounded-lg p-1">
            {Object.entries(RESPONDENT_LABELS).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setRespondent(key)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                  respondent === key
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ───── Forms ───── */}
        {openSchema ? (
          <DemoFormView
            schema={openSchema}
            answers={currentAnswers[openSchema.formKey] || {}}
            setAnswer={setAnswer}
            respondent={respondent}
            onClose={() => setOpenFormKey(null)}
          />
        ) : (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Forms</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {HFP_V3_SCHEMAS_LIST.map((schema) => (
                <button
                  key={schema.formKey}
                  type="button"
                  onClick={() => setOpenFormKey(schema.formKey)}
                  className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-5 py-4 shadow-sm hover:border-primary/40 hover:shadow transition-all text-left"
                >
                  <span className="text-sm font-medium text-gray-900">{schema.title}</span>
                  <StatusBadge progress={formProgress[schema.formKey] || 0} />
                </button>
              ))}
            </div>

            {/* ───── Simulation ───── */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Simulate All Answers (both respondents)</h3>
              <div className="flex flex-wrap items-end gap-4">
                <label className="text-xs text-gray-600">
                  Profile
                  <select
                    value={simProfile}
                    onChange={(e) => setSimProfile(e.target.value)}
                    className="mt-1 block rounded-lg border-gray-300 text-sm"
                  >
                    {Object.entries(SIMULATION_PROFILES).map(([key, p]) => (
                      <option key={key} value={key}>{p.label}</option>
                    ))}
                  </select>
                </label>
                <label className="text-xs text-gray-600">
                  Home / School agreement
                  <select
                    value={simAgreement}
                    onChange={(e) => setSimAgreement(e.target.value)}
                    className="mt-1 block rounded-lg border-gray-300 text-sm"
                  >
                    {Object.entries(CONTEXT_AGREEMENT_PRESETS).map(([key, p]) => (
                      <option key={key} value={key}>{p.label}</option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={simulateAll}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Simulate All Answers
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ───── Scoring ───── */}
        {!openSchema && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Scoring Engine</h2>
              <button
                type="button"
                onClick={runScoring}
                disabled={scoringLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {scoringLoading ? 'Running...' : 'Run Scoring Engine'}
              </button>
            </div>
            {scoringError && (
              <p className="text-sm text-red-600">{scoringError}</p>
            )}
            {scoringResult?.scoring && (
              <div className="space-y-4">
                {/* The clinician overview matrix — the same component the
                    Patient Overview renders, driven by the same payload. */}
                <HfpV3OverviewMatrix scoring={scoringResult.scoring} onOpenArea={null} />
                <JsonPanel title="Raw scoring payload (domains.hfpV3.scoring)" data={scoringResult.scoring} />
              </div>
            )}
          </div>
        )}

        {/* ───── Report generation ───── */}
        {!openSchema && scoringResult?.scoring && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Report</h2>
              <button
                type="button"
                onClick={generateReport}
                disabled={reportLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {reportLoading ? 'Generating… (can take a few minutes)' : 'Generate Report'}
              </button>
            </div>
            <div className="flex flex-wrap items-end gap-4">
              <label className="text-xs text-gray-600">
                Child name
                <input
                  type="text"
                  value={childName}
                  onChange={(e) => setChildName(e.target.value)}
                  className="mt-1 block rounded-lg border-gray-300 text-sm"
                />
              </label>
              <label className="text-xs text-gray-600">
                Pronouns
                <select
                  value={pronouns}
                  onChange={(e) => setPronouns(e.target.value)}
                  className="mt-1 block rounded-lg border-gray-300 text-sm"
                >
                  {PRONOUN_OPTIONS.map((p) => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-gray-600">
                Model
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="mt-1 block rounded-lg border-gray-300 text-sm"
                >
                  {llmModels.map((m) => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </select>
              </label>
            </div>
            {reportError && <p className="text-sm text-red-600">{reportError}</p>}
            {reportResult?.narratives?._llm_meta && (
              <p className="text-xs text-gray-500">
                Model: {Object.values(reportResult.narratives._llm_meta.model_labels || {}).join(', ')
                  || reportResult.narratives._llm_meta.model_id || 'LLM'}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Full-width report display — rendered by the same adapter +
          HfpV2Report pipeline as the clinician profile preview, so the demo
          always matches what the profile page shows. */}
      {!openSchema && reportModel && (
        <div className={fullScreen ? 'fixed inset-0 z-50 bg-white flex flex-col' : 'w-full'}>
          <div className={`flex items-center justify-end gap-2 px-4 py-2 border-t border-b border-gray-200 bg-white${fullScreen ? ' flex-shrink-0' : ''}`}>
            <button
              type="button"
              onClick={() => setFullScreen((v) => !v)}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {fullScreen ? 'Exit full screen' : 'Full screen'}
            </button>
          </div>
          <div className={fullScreen ? 'flex-1 min-h-0' : 'shadow-sm'}>
            <HfpV2Report
              report={reportModel}
              childName={(childName || '').trim() || 'the child'}
              pageMode={fullScreen}
              clinicianView
            />
          </div>
          <div className={`bg-[#4a2c5a] text-white text-center text-xs font-medium tracking-wide py-2.5 px-4${fullScreen ? ' flex-shrink-0' : ''}`}>
            This is not a medical report and does not constitute medical advice
          </div>
        </div>
      )}
    </div>
  );
}
